import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  customLabels,
  fieldMapping,
} from "../../data/buildingPermit/fieldDatas";
import { setBuildingState } from "../../store/actions/permitFormActions/buildingStateActions";
import { formikRef } from "../../store/formikStore/formikStore";
import { addUserFullDesignProfessionals } from "../../store/actions/userActions";
import { addSubUserFullDesignProfessionals } from "../../store/actions/subUserActions";
import { addPPUserFullDesignProfessionals } from "../../store/actions/privateProviderActions";
import { LoadingButton } from "@mui/lab";
import {
  TEMPLATE_CATEGORY_IDS,
  buildTemplateFieldValues,
  getTemplateCategoryConfig,
} from "../permitForms/utils/templateCategoryHelpers";

const FIXED_CATEGORY_FIELDS = {
  [TEMPLATE_CATEGORY_IDS.ENGINEER]: [
    { name: "engineer_name", field_id: "151e2dcc-750f-48de-9a28-565c09f16d86" },
    {
      name: "engineer_full_address",
      field_id: "f6bd69a2-2182-42a1-be90-37ec72773293",
    },
    {
      name: "engineer_street",
      field_id: "922a2a35-b269-433f-b57e-6236b26692e5",
    },
    { name: "engineer_city", field_id: "ddba1d90-2c3f-449c-aeca-c50bd76a6720" },
    {
      name: "engineer_state",
      field_id: "57e8a6ed-7868-466f-8003-c22261b1fb90",
    },
    { name: "engineer_zip", field_id: "047b004e-cd7f-4a2a-9ddb-ec2abb0da3c8" },
    {
      name: "engineer_phone",
      field_id: "e46f498f-35bc-4976-8f80-73ca630aa2ee",
    },
    {
      name: "engineer_email",
      field_id: "b727fadc-a3e1-4888-9079-3fe4d91ac155",
    },
    {
      name: "engineer_license_num",
      field_id: "abf612d2-7fa6-4bb9-ae2d-5010c9ded07c",
    },
  ],
  [TEMPLATE_CATEGORY_IDS.ARCHITECT]: [
    {
      name: "architect_name",
      field_id: "028fbd0e-3cfa-47b1-990b-de91cd2a75c2",
    },
    {
      name: "architect_phone",
      field_id: "d2c548be-7c74-429a-96a2-57abc42a85ff",
    },
    {
      name: "architect_email",
      field_id: "0c6db7ea-63f8-4721-ae32-5efb16c678b9",
    },
  ],
  [TEMPLATE_CATEGORY_IDS.DESIGN_PROFESSIONAL]: [
    {
      name: "design_pro_company",
      field_id: "596b33da-98c1-4605-a27a-c8c82a8b13e5",
    },
    {
      name: "design_pro_license_name",
      field_id: "0c1775bf-d193-47cd-a568-e964783b8186",
    },
    {
      name: "design_pro_license_num",
      field_id: "24e466d7-f93f-47bd-ad6a-6bac2f5f36fb",
    },
    {
      name: "design_pro_address",
      field_id: "d3fb1217-3a59-4890-a73c-0e988afe3e6b",
    },
    {
      name: "design_pro_street",
      field_id: "8f16e0b6-e0ce-44aa-9232-89c356cab490",
    },
    {
      name: "design_pro_city",
      field_id: "d0ad4618-a1f4-4a87-b24a-c7cce9769e32",
    },
    {
      name: "design_pro_state",
      field_id: "dfb2e43a-75a3-4424-95ba-d03a3240ba5d",
    },
    {
      name: "design_pro_zip",
      field_id: "6a354b46-623b-4ecc-9fbe-ef2a41b59a69",
    },
    {
      name: "design_pro_phone",
      field_id: "b41cfe30-1382-49c7-8a6a-e32674af9a29",
    },
    {
      name: "design_pro_fax",
      field_id: "b5a724b4-d442-40e2-a0da-8d9338387615",
    },
    {
      name: "design_pro_email",
      field_id: "24d31aa9-0053-45e3-ad5d-2879c7e7986b",
    },
  ],
};

const CATEGORY_CANONICAL_FIELD_NAMES = {
  [TEMPLATE_CATEGORY_IDS.ENGINEER]: {
    design_pro_company: "engineer_name",
    design_pro_license_name: "engineer_name",
    design_pro_license_num: "engineer_license_num",
    design_pro_address: "engineer_full_address",
    design_pro_phone: "engineer_phone",
    design_pro_email: "engineer_email",
  },
  [TEMPLATE_CATEGORY_IDS.ARCHITECT]: {
    design_pro_company: "architect_name",
    design_pro_license_name: "architect_name",
    design_pro_phone: "architect_phone",
    design_pro_email: "architect_email",
  },
  [TEMPLATE_CATEGORY_IDS.DESIGN_PROFESSIONAL]: Object.keys(fieldMapping).reduce(
    (names, name) => ({ ...names, [name]: name }),
    {},
  ),
};

const REQUIRED_CATEGORY_FIELDS = {
  [TEMPLATE_CATEGORY_IDS.ENGINEER]: [
    "engineer_name",
    "engineer_full_address",
    "engineer_phone",
    "engineer_email",
    "engineer_license_num",
  ],
  [TEMPLATE_CATEGORY_IDS.ARCHITECT]: [
    "architect_name",
    "architect_phone",
    "architect_email",
  ],
  [TEMPLATE_CATEGORY_IDS.DESIGN_PROFESSIONAL]: [
    "design_pro_company",
    "design_pro_license_name",
    "design_pro_license_num",
    "design_pro_address",
    "design_pro_phone",
    "design_pro_email",
  ],
};

const getFieldName = (field) => field?.name || field?.field_name;

const getFieldLabel = (field) => {
  const name = getFieldName(field);
  return (
    customLabels[name] ||
    field?.label ||
    field?.field_label ||
    field?.title ||
    String(name || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const DesignProAddInfoModal = () => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { addingNewDesignPro, designProLoading, activeTemplateCategory } =
    useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [fieldValues, setFieldValues] = useState({
    design_pro_company: "",
    design_pro_license_name: "",
    design_pro_license_num: "",
    design_pro_address: "",
    design_pro_phone: "",
    design_pro_fax: "",
    design_pro_email: "",
  });

  const categoryId = activeTemplateCategory?.categoryId;

  const resolvedFields = useMemo(() => {
    return (
      FIXED_CATEGORY_FIELDS[categoryId] ||
      Object.keys(fieldMapping).map((name) => ({ name }))
    );
  }, [categoryId]);

  const resolvedFieldNames = useMemo(
    () => resolvedFields.map(getFieldName).filter(Boolean),
    [resolvedFields],
  );

  const canonicalFieldNames = useMemo(() => {
    return (
      CATEGORY_CANONICAL_FIELD_NAMES[categoryId] ||
      Object.keys(fieldMapping).reduce(
        (names, name) => ({ ...names, [name]: name }),
        {},
      )
    );
  }, [categoryId]);

  const getCanonicalFieldValue = (canonicalName) => {
    const resolvedName = canonicalFieldNames[canonicalName];
    return resolvedName ? (fieldValues[resolvedName] ?? "") : "";
  };

  useEffect(() => {
    if (!addingNewDesignPro || !resolvedFieldNames.length) return;

    setFieldValues((prev) => ({
      ...prev,
      ...resolvedFieldNames.reduce(
        (values, name) => ({ ...values, [name]: prev[name] ?? "" }),
        {},
      ),
    }));
  }, [addingNewDesignPro, resolvedFieldNames]);

  useEffect(() => {
    const callFunc = () => {
      const requiredFields =
        REQUIRED_CATEGORY_FIELDS[categoryId] ||
        Object.entries(canonicalFieldNames)
          .filter(([canonicalName]) => canonicalName !== "design_pro_fax")
          .map(([, resolvedName]) => resolvedName);

      const error = requiredFields.some((fieldName) => {
        const val = String(fieldValues[fieldName] ?? "");
        const isPhoneField = /(^|_)(phone|mobile)($|_)/i.test(fieldName);

        return isPhoneField ? val.length < 12 : val.trim().length < 3;
      });

      setSubmitDisabled(error);
    };

    callFunc();
  }, [canonicalFieldNames, categoryId, fieldValues]);

  const runFieldCange = (event) => {
    const { name, value } = event.target;
    setFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ designProLoading: true }));

      const categoryFieldValues = resolvedFieldNames.reduce(
        (values, name) => ({
          ...values,
          [name]: fieldValues[name] ?? "",
        }),
        {},
      );
      const fieldValuesById = resolvedFields.reduce((values, field) => {
        const fieldId = field?.field_id;
        const fieldName = getFieldName(field);

        return fieldId && fieldName
          ? { ...values, [fieldId]: fieldValues[fieldName] ?? "" }
          : values;
      }, {});
      const config = getTemplateCategoryConfig(categoryId);

      const values = {
        ...categoryFieldValues,
        ...(categoryId && Object.keys(fieldValuesById).length
          ? {
              category_id: categoryId,
              field_values: fieldValuesById,
            }
          : {}),
        companyName: getCanonicalFieldValue("design_pro_company"),
        licenseName: getCanonicalFieldValue("design_pro_license_name"),
        licenseNumber: getCanonicalFieldValue("design_pro_license_num"),
        address: getCanonicalFieldValue("design_pro_address"),
        phone: getCanonicalFieldValue("design_pro_phone"),
        fax: getCanonicalFieldValue("design_pro_fax"),
        email: getCanonicalFieldValue("design_pro_email"),
        addData: true, // Flag to indicate we're adding new data
      };

      // Call API to save to database
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/modify-design-professional`,
        {
          values,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        const designProfessionals = (
          resp?.data?.design_professionals || []
        ).map((professional) => {
          const isNewProfessional =
            professional.companyName?.toLowerCase() ===
              values.companyName.toLowerCase() &&
            professional.email?.toLowerCase() === values.email.toLowerCase();

          return isNewProfessional
            ? {
                ...professional,
                ...categoryFieldValues,
                ...(categoryId && Object.keys(fieldValuesById).length
                  ? {
                      category_id: professional.category_id || categoryId,
                      field_values:
                        professional.field_values || fieldValuesById,
                    }
                  : {}),
              }
            : professional;
        });

        // Update Redux store with the saved design professionals from the database
        if (currentSession === "privateProviderSession") {
          await dispatch(addPPUserFullDesignProfessionals(designProfessionals));
        } else if (currentSession === "subUserSession") {
          dispatch(addSubUserFullDesignProfessionals(designProfessionals));
        } else if (currentSession === "userSession") {
          dispatch(addUserFullDesignProfessionals(designProfessionals));
        }

        // Find the newly added design professional to set it in the form
        const newDesignPro = designProfessionals.find(
          (pro) =>
            pro.companyName?.toLowerCase() ===
              values.companyName.toLowerCase() &&
            pro.email?.toLowerCase() === values.email.toLowerCase(),
        );

        if (newDesignPro?.id) {
          const activeDocumentFields = activeTemplateCategory?.fields?.length
            ? activeTemplateCategory.fields
            : resolvedFields;
          const formikPatchValues = config
            ? buildTemplateFieldValues(
                newDesignPro,
                activeDocumentFields,
                config,
              )
            : {};

          formik?.setValues((prevValues) => ({
            ...prevValues,
            ...categoryFieldValues,
            ...formikPatchValues,
            [config?.selectorName || "designProfessional"]: newDesignPro.id,
          }));
        }

        await dispatch(
          setBuildingState({
            addingNewDesignPro: false,
            activeTemplateCategory: null,
          }),
        );
      } else {
        console.error("❌ API returned success: false", resp?.data);
        // Handle error cases
        if (resp?.data?.duplicated) {
          console.error("❌ Duplicate:", resp?.data?.message);
          alert(resp?.data?.message);
        } else if (resp?.data?.is_locked) {
          console.error("❌ Locked:", resp?.data?.message);
          alert(resp?.data?.message);
        }
      }
    } catch (e) {
      console.error("❌ Error adding design professional:", e);
      console.error("❌ Error details:", e?.response?.data || e?.message);
      alert("Failed to add design professional. Please try again.");
    } finally {
      await dispatch(setBuildingState({ designProLoading: false }));
    }
  };

  const cleanPhone = (e) => {
    const { name, value } = e.target;

    let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

    // If the number of digits is greater than 10, slice to 10 digits
    if (digitsOnly?.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }

    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    setFieldValues((prev) => ({
      ...prev,
      [name]: formattedPhoneNumber,
    }));
  };

  return (
    <Modal
      open={addingNewDesignPro}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent", // Add a semi-transparent backdrop
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%", // Adjust width for smaller screens
          maxWidth: 600, // Set a maximum width
          bgcolor: "background.paper",
          boxShadow: "2px 10px 10px 5px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" align="center" color="primary">
            Add New Design Professional
          </Typography>
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            maxHeight: "60vh", // Limit height to 60% of the viewport
            overflowY: "auto", // Enable scrolling when content overflows
            p: 2,
          }}
        >
          <>
            {resolvedFields.map((fieldConfig, i) => {
              const key = getFieldName(fieldConfig);
              const isPhoneField =
                key === "design_pro_phone" ||
                key === "design_pro_fax" ||
                /(^|_)(phone|mobile|fax)($|_)/i.test(key);

              return (
                <TextField
                  autoComplete="off"
                  key={key + i}
                  fullWidth
                  onKeyUp={(event) => {
                    if (isPhoneField) cleanPhone(event);
                  }}
                  margin="normal"
                  variant="filled"
                  label={getFieldLabel(fieldConfig)}
                  name={key}
                  value={fieldValues[key] ?? ""}
                  onChange={runFieldCange}
                  onBlur={(event) => {
                    formik?.handleBlur(event);
                    formik?.setFieldTouched(key, true, true);
                  }}
                  error={formik?.touched[key] && Boolean(formik?.errors[key])}
                  helperText={formik?.touched[key] && formik?.errors[key]}
                />
              );
            })}
          </>
        </Box>

        {/* Footer Section with Buttons */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            disabled={designProLoading}
            fullWidth
            onClick={() => {
              dispatch(
                setBuildingState({
                  addingNewDesignPro: false,
                  activeTemplateCategory: null,
                }),
              );
            }}
            color="error"
            variant="contained"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={designProLoading}
            disabled={submitDisabled}
            fullWidth
            onClick={handleAddNew}
            color="success"
            variant="contained"
          >
            Add
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(DesignProAddInfoModal);
